import { useEffect, useRef, useState } from 'react';
import { Copy } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { useEffectiveRecipients } from '@/hooks/useMail';
import { formatDateTime } from '@/lib/format';
import type { EffectiveRecipient, RecipientOrigin } from '@/types/api';

interface EffectiveRecipientsCardProps {
  customerId: string;
}

/**
 * Sicht "Wer bekommt Wartungsmails": wirksame Adressen, Herkunft und
 * Unterdrückungen. Die alte Leerzeile im Kontakt-Tab hat "keine Mails"
 * auch dann behauptet, wenn der Kundenfeld-Fallback greift.
 */
export function EffectiveRecipientsCard({ customerId }: EffectiveRecipientsCardProps): JSX.Element {
  const query = useEffectiveRecipients(customerId);

  if (query.isLoading) return <SkeletonRows rows={2} cols={1} />;
  if (query.isError) return <ErrorAlert error={query.error} />;
  if (!query.data) return <ErrorAlert error="Keine Daten." />;

  const data = query.data;
  const syncedAt = data.contacts_synced_at;

  return (
    <Card
      title="Wer bekommt Wartungsmails"
      data-testid="effective-recipients-card"
      {...(syncedAt
        ? {
            description: (
              <span data-testid="effective-recipients-synced">
                Kontakte zuletzt aus InvoiceNinja geholt: {formatDateTime(syncedAt)}
              </span>
            ),
          }
        : {})}
      {...(data.recipients.length > 0
        ? {
            actions: <CopyAddressesButton emails={data.recipients.map((r) => r.email)} />,
          }
        : {})}
    >
      {data.opt_out ? (
        <div
          className="mb-3 flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3"
          data-testid="effective-recipients-summary"
        >
          <Badge tone="warning">Opt-out</Badge>
          <p className="text-sm">
            Opt-out: der Kunde hat Wartungsmails abbestellt. Es geht nichts raus.
          </p>
        </div>
      ) : (
        <p className="mb-3 text-sm" data-testid="effective-recipients-summary">
          {summaryText(data.recipients.length)}
        </p>
      )}

      {data.redirect_active ? (
        <div className="mb-3" data-testid="effective-recipients-redirect">
          <Badge tone="warning">
            Mail-Testmodus aktiv: alles geht an {data.redirect_address ?? 'die Testadresse'}.
          </Badge>
        </div>
      ) : null}

      {/* Kein Testmodus-Stand: schweigen wäre hier die gefährlichere
          Aussage - wer die Ankündigung auslöst, hält sonst umgeleitete
          Mails für zugestellt. */}
      {data.redirect_unknown ? (
        <div className="mb-3" data-testid="effective-recipients-redirect-unknown">
          <Badge tone="warning">
            Mail-Testmodus unbekannt: nicht gesichert, ob die Mails beim Kunden landen.
          </Badge>
        </div>
      ) : null}

      <ul className={data.opt_out ? 'opacity-60' : undefined}>
        {data.recipients.map((recipient, index) => (
          <RecipientItem key={`${recipient.email}-${index}`} recipient={recipient} index={index} />
        ))}
      </ul>

      {data.suppressed.length > 0 ? (
        <div className="mt-4" data-testid="effective-recipients-suppressed">
          <h3 className="mb-2 text-sm font-medium">Bekommt keine Mails</h3>
          <ul className="flex flex-col gap-2 text-sm">
            {data.suppressed.map((row) => (
              <li key={row.contact_id}>
                <div className="font-medium">{row.display_name}</div>
                {row.email ? <div className="font-mono text-xs">{row.email}</div> : null}
                <div className="text-xs text-muted">{row.reason_text}</div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}

function summaryText(count: number): string {
  if (count === 0) {
    // Nur dieser Fall darf "keine Mails" behaupten - nicht der Fallback.
    return 'Für diesen Kunden werden keine Mails erzeugt - es ist keine Adresse hinterlegt.';
  }
  if (count === 1) {
    return 'Es geht eine Mail an 1 Adresse.';
  }
  return `Es gehen Mails an ${count} Adressen.`;
}

function originBadge(origin: RecipientOrigin): JSX.Element {
  if (origin === 'contact_invoice_ninja') {
    return <Badge tone="primary">InvoiceNinja</Badge>;
  }
  if (origin === 'contact_manual') {
    return <Badge tone="neutral">manuell</Badge>;
  }
  return <Badge tone="warning">Kundenfeld</Badge>;
}

function RecipientItem({
  recipient,
  index,
}: {
  recipient: EffectiveRecipient;
  index: number;
}): JSX.Element {
  const stand = recipient.last_synced_at
    ? `, Stand ${formatDateTime(recipient.last_synced_at)}`
    : '';
  return (
    <li className="py-2" data-testid={`effective-recipient-${index}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono">{recipient.email}</span>
        {originBadge(recipient.origin)}
        {recipient.is_primary ? <Badge tone="success">Hauptkontakt</Badge> : null}
      </div>
      <div className="text-xs text-muted">
        {recipient.reason_text}
        {stand}
      </div>
    </li>
  );
}

function CopyAddressesButton({ emails }: { emails: string[] }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const onCopy = (): void => {
    const text = emails.join('; ');
    try {
      const clipboard = navigator.clipboard;
      if (!clipboard || typeof clipboard.writeText !== 'function') {
        return;
      }
      void clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
          }
          timerRef.current = setTimeout(() => {
            setCopied(false);
            timerRef.current = null;
          }, 2000);
        })
        .catch(() => {
          /* jsdom und fehlende Berechtigung: still bleiben. */
        });
    } catch {
      /* still bleiben */
    }
  };

  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      leftIcon={<Copy className="size-4" />}
      onClick={onCopy}
      data-testid="effective-recipients-copy"
    >
      {copied ? 'Kopiert' : 'Adressen kopieren'}
    </Button>
  );
}
